// ABOUTME: Integrated jobs + quotes functions backed by the Worker API.
// ABOUTME: Creates a job with multiple quote requests in one operation.

import { api } from "@/lib/supabase/client";
import { UserJob, Quote } from "@/lib/types";
import { withErrorHandling } from "@/lib/errors";
import { requestQuote } from "@/lib/supabase/quotes";
import { CreateJobWithQuotesParams, JobWithQuotesResponse } from "@/lib/types/quote-types";
import { debug } from "@/lib/utils";

export const createJobWithQuotes = withErrorHandling(
  async (userId: string, params: CreateJobWithQuotesParams): Promise<JobWithQuotesResponse> => {
    if (!params.title) throw new Error('Job title is required');
    if (!params.description) throw new Error('Job description is required');
    if (!params.trade_ids || params.trade_ids.length === 0) throw new Error('At least one tradesperson must be selected');

    const { job } = await api.post<{ job: UserJob }>('/jobs', {
      title: params.title,
      description: params.description,
      category: params.category,
      status: 'quote_requested',
      trade_id: params.primary_trade_id,
    });

    const quotePromises = params.trade_ids.map(tradeId =>
      requestQuote(userId, { job_id: job.id, trade_id: tradeId })
    );
    const quotesWithNulls = await Promise.all(quotePromises);
    const quotes = quotesWithNulls.filter((q): q is Quote => q !== null);

    if (quotes.length < params.trade_ids.length) {
      debug.warn(`Some quote requests failed: ${quotes.length} of ${params.trade_ids.length} succeeded`);
    }

    return { job, quotes };
  },
  null,
  'Failed to create job with quotes'
);

export const getJobQuoteStatistics = withErrorHandling(
  async (_userId: string) => {
    const [jobsRes, quotesRes] = await Promise.all([
      api.get<{ jobs: Array<{ id: string; status: string }> }>('/jobs'),
      api.get<{ quotes: Array<{ id: string; phase: string; job_id: string | null }> }>('/quotes'),
    ]);

    const jobs = jobsRes.jobs || [];
    const quotes = quotesRes.quotes || [];

    const scheduledJobIds = new Set(
      jobs.filter(j => j.status === 'scheduled' || j.status === 'completed').map(j => j.id)
    );

    const jobsWithQuotes = new Set(quotes.map(q => q.job_id).filter(Boolean)).size;

    return {
      totalJobs: jobs.length,
      quoteRequestedJobs: jobs.filter(j => j.status === 'quote_requested').length,
      completedJobs: jobs.filter(j => j.status === 'completed').length,
      totalQuotes: quotes.length,
      pricedQuotes: quotes.filter(q => q.phase === 'priced').length,
      declinedQuotes: quotes.filter(q => q.phase === 'declined').length,
      pendingQuotes: quotes.filter(q => q.phase === 'requested' || q.phase === 'visit_booked').length,
      acceptedQuotes: quotes.filter(q => q.job_id && scheduledJobIds.has(q.job_id)).length,
      averageQuotesPerJob: jobsWithQuotes ? quotes.length / jobsWithQuotes : 0,
    };
  },
  {
    totalJobs: 0, quoteRequestedJobs: 0, completedJobs: 0,
    totalQuotes: 0, pricedQuotes: 0, declinedQuotes: 0,
    pendingQuotes: 0, acceptedQuotes: 0, averageQuotesPerJob: 0,
  },
  'Failed to get job quote statistics'
);
